// mapbox-lib
import * as CommonSelectors from '@/vue3/mapbox/lib/commonSelectors';
import * as Constants from './constants';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import isEventAtCoordinates from '@/vue3/mapbox/lib/isEventAtCoordinates';
import doubleClickZoom from '@/vue3/mapbox/lib/doubleClickZoom';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

import { useStore } from '@/store';
import { getPolygonEdgeLabelFeature } from './supportFunctions';

const store = useStore();

let isMeters;

const DrawPolygon = MapboxDraw.modes.draw_polygon;
const DrawPolygonMode = { ...DrawPolygon };

const createVertexPoly = (parentId, coordinates, path, selected) => {
    return {
        type: Constants.geojsonTypes.FEATURE,
        properties: {
            meta: Constants.meta.VERTEX,
            parent: parentId,
            coord_path: path,
            active: selected
                ? Constants.activeStates.ACTIVE
                : Constants.activeStates.INACTIVE,
        },
        geometry: {
            type: Constants.geojsonTypes.POINT,
            coordinates,
        },
    };
};

DrawPolygonMode.onKeyUp = function ({ polygon: { id } }, e) {
    const isEscapeKey = CommonSelectors.isEscapeKey(e);

    if (!isEscapeKey) return;

    this.deleteFeature([id], { silent: true });
    this.changeMode('DrawPolygonMode');
};

DrawPolygonMode.toDisplayFeatures = function (state, geojson, display) {
    isMeters = store.state.isMeters;
    const isActivePolygon = geojson.properties.id === state.polygon.id;
    geojson.properties.active = isActivePolygon
        ? Constants.activeStates.ACTIVE
        : Constants.activeStates.INACTIVE;
    if (!isActivePolygon) return display(geojson);

    // Don't render a polygon until it has two positions
    // (and a 3rd which is just the first repeated)
    if (geojson.geometry.coordinates.length === 0) return;

    const coordinateCount = geojson.geometry.coordinates[0].length;
    // 2 coordinates after selecting a draw type
    // 3 after creating the first point
    if (coordinateCount < 3) {
        return;
    }

    //------------------------------------------BEGIN-EDGE-LABEL-----------------------------------------------------------------
    for (let i = 2; i < coordinateCount + 1; i++) {
        let point_1_idx = i - 2;
        let point_2_idx = i - 1;

        if (i == coordinateCount) {
            //For the length of the last line
            if (coordinateCount > 3) {
                point_2_idx = 0;
            } else {
                continue;
            }
        }

        const currentLineLabel = getPolygonEdgeLabelFeature(
            geojson,
            point_1_idx,
            point_2_idx,
            isMeters,
        );

        display(currentLineLabel);
    }
    //--------------------------------------------END-EDGE-LABEL-----------------------------------------------------------------

    //------------------------------------------BEGIN-POLYGON--------------------------------------------------------------------

    geojson.properties.meta = Constants.meta.FEATURE;
    display(
        createVertexPoly(
            state.polygon.id,
            geojson.geometry.coordinates[0][0],
            '0.0',
            false,
        ),
    );
    if (coordinateCount > 3) {
        // Add a start position marker to the map, clicking on this will finish the feature
        // This should only be shown when we're in a valid spot
        const endPos = geojson.geometry.coordinates[0].length - 3;
        display(
            createVertexPoly(
                state.polygon.id,
                geojson.geometry.coordinates[0][endPos],
                `0.${endPos}`,
                false,
            ),
        );
    }
    if (coordinateCount <= 4) {
        // If we've only drawn two positions (plus the closer),
        // make a LineString instead of a Polygon
        const lineCoordinates = [
            [
                geojson.geometry.coordinates[0][0][0],
                geojson.geometry.coordinates[0][0][1],
            ],
            [
                geojson.geometry.coordinates[0][1][0],
                geojson.geometry.coordinates[0][1][1],
            ],
        ];
        // create an initial vertex so that we can track the first point on mobile devices
        display({
            type: Constants.geojsonTypes.FEATURE,
            properties: geojson.properties,
            geometry: {
                coordinates: lineCoordinates,
                type: Constants.geojsonTypes.LINE_STRING,
            },
        });
        if (coordinateCount === 3) {
            return;
        }
    }
    // render the Polygon
    return display(geojson);
    //--------------------------------------------END-POLYGON--------------------------------------------------------------------
};

export default DrawPolygonMode;
